import React, { FC, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";

type Props = {
  children: React.ReactElement | React.ReactNode;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  cancelButtonRef?: React.RefObject<HTMLButtonElement>;
  setIsEditing?: (value: boolean) => void;
  title: string;
  onClick?: any;
  onClose?: any;
  width?: string;
  padding?: string;
};

export const Modal: FC<Props> = ({
  children,
  showModal,
  setShowModal,
  onClick,
  cancelButtonRef,
  setIsEditing = () => {},
  title,
  onClose,
  width,
  padding,
}) => {
  const handleClose = () => {
    onClick && onClick();
    setShowModal(false);
    setIsEditing && setIsEditing(false);
    onClose && onClose();
  };

  return (
    <Transition.Root show={showModal} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        open={showModal}
        onClose={() => handleClose()}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Backdrop className="fixed inset-0 bg-ink/30 backdrop-blur-[2px] transition-opacity" />
        </Transition.Child>

        {/* Modal panel */}
        <div className="flex items-center justify-center min-h-screen px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-250"
            enterFrom="opacity-0 scale-95 translate-y-2"
            enterTo="opacity-100 scale-100 translate-y-0"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100 translate-y-0"
            leaveTo="opacity-0 scale-95 translate-y-2"
          >
            <Dialog.Panel
              className={`
                relative bg-white rounded-2xl shadow-card-lg border border-line text-left
                transform transition-all w-full my-8
                ${width ? width : "max-w-2xl"}
              `}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-line">
                <Dialog.Title className="text-base font-semibold text-ink leading-tight">
                  {title}
                </Dialog.Title>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex items-center justify-center w-7 h-7 rounded-lg text-ink-muted hover:text-ink hover:bg-surface-muted transition-all duration-150 focus:outline-none"
                  aria-label="Close"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className={padding ? padding : "px-6 py-5"}>
                {children}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
